/* eslint-disable max-lines */
import {
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import {
  IOpenReturnEntitlement,
  IReturnTripSelection,
  OpenReturnStatusEnum
} from "../../../interfaces/reservations/Reservations.types";
import { selectOpenReturnAdmin } from "../../../services/Reservations.service";
import Button from "../../ui/Button";
import DayPicker from "../../ui/datepicker/day/DayPicker";
import IconBtn from "../../ui/IconBtn";
import Input from "../../ui/input/Input";
import Modal from "../../ui/Modal";
import SuperAdmin from "../../users/permissions/SuperAdmin";

interface Props {
  uniqueJourneyId: string;
  openReturn: IOpenReturnEntitlement;
  // Refresh the reservation detail after a successful selection.
  onSelected?: () => void;
}

// A single editable return leg in the picker: trip id + date only. Stations are
// always derived server-side from the trip line's endpoints (full-line return).
type LegDraft = {
  tripId: string;
  journeyDate: string;
};

const resolveError = (err: unknown, fallback: string) => {
  const e = err as { response?: { data?: unknown } };
  if (typeof e?.response?.data === "string" && e.response.data) {
    return e.response.data;
  }
  return fallback;
};

const SelectOpenReturnAction: FC<Props> = ({
  uniqueJourneyId,
  openReturn,
  onSelected
}) => {
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Opt-out: pre-ticked so the customer is notified by default.
  const [shouldNotifyUser, setShouldNotifyUser] = useState(true);

  const seedLeg = (): LegDraft => ({
    tripId: "",
    journeyDate: dayjs().format("YYYY-MM-DD")
  });

  const [legs, setLegs] = useState<LegDraft[]>([seedLeg()]);

  // Only offer the action while the entitlement is still actionable.
  if (openReturn.status !== OpenReturnStatusEnum.Pending) return null;

  const reset = () => {
    setLegs([seedLeg()]);
    setSubmitting(false);
    setShouldNotifyUser(true);
    setShow(false);
  };

  const updateLeg = (index: number, patch: Partial<LegDraft>) => {
    setLegs((prev) =>
      prev.map((leg, i) => (i === index ? { ...leg, ...patch } : leg))
    );
  };

  const addLeg = () => setLegs((prev) => [...prev, seedLeg()]);

  const removeLeg = (index: number) =>
    setLegs((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    const trips: IReturnTripSelection[] = legs.map((leg) => ({
      tripId: Number(leg.tripId),
      // Always null: the backend derives the leg's endpoints from the trip line.
      departureStationId: null,
      arrivalStationId: null,
      // Anchor at midday in the app's zone (Europe/Belgrade) so the server-side
      // date math is timezone-stable regardless of the admin's browser locale.
      journeyDateTime: dayjs
        .tz(leg.journeyDate, "Europe/Belgrade")
        .hour(12)
        .toISOString()
    }));

    if (trips.some((t) => !t.tripId || t.tripId <= 0)) {
      toast.error("Plotëso ID e orarit për çdo segment.", {
        position: "top-right"
      });
      return;
    }

    setSubmitting(true);
    try {
      await selectOpenReturnAdmin(uniqueJourneyId, { trips }, shouldNotifyUser);
      toast.success("Udhëtimi i kthimit u zgjodh pa pagesë.", {
        position: "top-right"
      });
      onSelected?.();
      reset();
    } catch (err) {
      toast.error(
        resolveError(err, "Ndodhi një gabim gjatë zgjedhjes së kthimit."),
        { position: "top-right" }
      );
      setSubmitting(false);
    }
  };

  return (
    <SuperAdmin>
      <div className="bg-white rounded-lg p-4 mt-4 border border-orange-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="font-medium">Open Return</span>{" "}
            <span className="text-gray-500">
              {openReturn.returnFromStationName} →{" "}
              {openReturn.returnToStationName} · {openReturn.ticketCount} biletë
              · skadon {dayjs(openReturn.expiresAtUtc).format("DD/MM/YYYY")}
            </span>
          </div>
          <Button
            btnType="custom"
            padding="px-3 py-1.5"
            className="border border-primary text-primary text-xs"
            onClick={() => setShow(true)}
          >
            Zgjidh kthimin (pa pagesë)
          </Button>
        </div>
      </div>

      <Modal
        show={show}
        position="center"
        onClose={() => setShow(false)}
        hasHeader={false}
        calcHeight={false}
        modalWidth="560px"
      >
        <div className="flex justify-center mt-6 font-medium">
          Zgjidh udhëtimin e kthimit (superadmin)
          <IconBtn
            className="absolute right-2 top-2"
            onClick={() => setShow(false)}
          >
            <XMarkIcon className="w-4 h-4" strokeWidth={2} />
          </IconBtn>
        </div>

        <div className="px-4 mt-4 text-sm text-gray-600">
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-md p-2 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 shrink-0" />
            <p className="text-xs">
              Krijon biletat e kthimit pa pagesë. Çdo diferencë çmimi mbi shumën
              e parapaguar mbulohet (comp) dhe dritarja e skadimit anashkalohet.
            </p>
          </div>

          {legs.map((leg, index) => (
            <div
              key={index}
              className="border rounded-md p-3 mb-3 bg-gray-50 relative"
            >
              {legs.length > 1 && (
                <IconBtn
                  className="absolute right-1 top-1"
                  onClick={() => removeLeg(index)}
                >
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </IconBtn>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="ID e orarit"
                  type="number"
                  paddingType="small"
                  value={leg.tripId}
                  onChange={(e) => updateLeg(index, { tripId: e.target.value })}
                />
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Data e kthimit
                  </label>
                  <DayPicker
                    // onlyModal renders the calendar in its own portal layer so it
                    // is not clipped by the overflow-hidden parent Modal.
                    onlyModal
                    date={dayjs(leg.journeyDate).toDate()}
                    onChangeDate={(date) =>
                      updateLeg(index, {
                        journeyDate: dayjs(date).format("YYYY-MM-DD")
                      })
                    }
                  />
                </div>
              </div>
            </div>
          ))}

          <Button
            btnType="custom"
            padding="px-2 py-1"
            className="border border-gray-300 text-xs flex items-center gap-1"
            onClick={addLeg}
          >
            <PlusIcon className="w-4 h-4" /> Shto segment
          </Button>

          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="open-return-notify"
              className="cursor-pointer"
              checked={shouldNotifyUser}
              onChange={(e) => setShouldNotifyUser(e.target.checked)}
            />
            <label
              htmlFor="open-return-notify"
              className="text-xs cursor-pointer select-none"
            >
              Njofto klientin për zgjedhjen e kthimit
            </label>
          </div>
        </div>

        <div className="flex gap-2 justify-end p-4">
          <Button
            btnType="custom"
            className="border border-gray-300"
            onClick={() => setShow(false)}
          >
            Anulo
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Duke kryer..." : "Konfirmo"}
          </Button>
        </div>
      </Modal>
    </SuperAdmin>
  );
};

export default SelectOpenReturnAction;
